import { memo, useState } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  Gamepad2,
  GripVertical,
  Lock,
  UserMinus,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';

export interface PasoVista {
  id: number;
  name: string;
  /** Lleva juego: al elegirlo, el panel ofrece cuál poner. */
  hasGame?: boolean;
  /** Sus preguntas, para poder ocultarlas una a una. Las declara la previa. */
  components?: Array<{ id: number; label: string; type: string; required: boolean }>;
  /**
   * Paso LEGAL: consentimiento o puerta de edad. Lo gobierna el sistema — la
   * capa legal decide qué se pinta a partir de lo que la encuesta recoge y de
   * la página Legal de la marca — y el ojo se vuelve candado. Ocultarlo a mano
   * sería poder dejar un despliegue sin consentimiento con un clic.
   */
  legal?: boolean;
}

interface Props {
  /** Todos los pasos de la encuesta, en el orden que declara la previa. */
  pasos: PasoVista[];
  /** Los que este evento no enseña. */
  ocultos: number[];
  /**
   * Las preguntas que este evento no enseña, de cualquier paso.
   *
   * Una lista plana y no un mapa por paso: es lo que viaja al despliegue y lo
   * que el runtime necesita para sacarlas del árbol. Agruparlas aquí obligaría
   * a aplanarlas en cada envío y a mantener las dos formas en sincronía.
   */
  componentesOcultos: number[];
  /** Orden propio del evento. Vacío = el de la encuesta. */
  orden: number[];
  seleccionado: number | null;
  /**
   * Cuánta gente se fue en cada paso, por id.
   *
   * Va aquí y no en una pantalla de métricas aparte porque la pregunta que
   * responde —«¿qué arreglo?»— se hace mirando el paso, no una tabla: el sitio
   * donde se abandona es el sitio donde hay que tocar algo.
   */
  abandonos?: Record<number, number>;
  onSeleccionar: (stepId: number) => void;
  onOrden: (orden: number[]) => void;
  onOcultos: (ocultos: number[]) => void;
  onComponentesOcultos: (ocultos: number[]) => void;
}

/**
 * Los pasos de la encuesta, tal como los ve este evento.
 *
 * Se puede **reordenar y ocultar**, y nada más. No hay añadir, ni borrar, ni
 * entrar dentro de un paso: eso es diseño de la encuesta y vive en Xurvey. El
 * límite no es de permisos, es de contrato (protocolo §3) — si cada evento
 * reescribe las preguntas, las respuestas de dos eventos dejan de poder
 * compararse, que es justo para lo que se recogen.
 *
 * **Ocultar no borra.** El paso sigue en la encuesta y en los demás
 * despliegues; aquí sólo se decide si esta activación lo enseña. Por eso los
 * ocultos siguen en la lista, atenuados y con su ojo tachado, en vez de
 * desaparecer: un paso que se esfuma al ocultarlo no hay forma de recuperarlo
 * sin adivinar dónde estaba.
 */
function StepsPaneBase({
  pasos,
  ocultos,
  componentesOcultos,
  orden,
  seleccionado,
  abandonos,
  onSeleccionar,
  onOrden,
  onOcultos,
  onComponentesOcultos,
}: Props) {
  const t = useT();
  const sensores = useSensors(
    // Un umbral de 6px: sin él, cada clic para seleccionar un paso empieza un
    // arrastre y la lista se reordena sola al soltar.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // El orden del evento manda; lo que no nombre va detrás, en el de la
  // encuesta. Si no fuera así, añadir un paso en Xurvey lo dejaría invisible
  // en todos los eventos ya configurados y nadie entendería por qué.
  const ordenados = [
    ...orden.map((id) => pasos.find((p) => p.id === id)).filter((p): p is PasoVista => !!p),
    ...pasos.filter((p) => !orden.includes(p.id)),
  ];

  // El peor paso se marca, no sólo el número: en una lista de cinco cifras
  // pequeñas nadie ve cuál es la mala sin compararlas a mano.
  const peor = Math.max(0, ...Object.values(abandonos ?? {}));

  const alSoltar = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const desde = ordenados.findIndex((p) => p.id === active.id);
    const hasta = ordenados.findIndex((p) => p.id === over.id);
    if (desde < 0 || hasta < 0) return;
    onOrden(arrayMove(ordenados, desde, hasta).map((p) => p.id));
  };

  /*
   * Sin ancho propio: lo fija la columna que la aloja. Con 240 px fijos dentro
   * de una columna de 232 útiles, y 15 menos en cuanto salía la barra de
   * scroll, la lista desbordaba y el `overflow-x-hidden` de la columna se
   * llevaba justo la columna de los ojos.
   */
  return (
    <aside className="flex w-full min-w-0 flex-col gap-2">
      <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--color-fg-muted)]">
        {t('designJourney.steps')}
      </p>

      <DndContext sensors={sensores} collisionDetection={closestCenter} onDragEnd={alSoltar}>
        <SortableContext items={ordenados.map((p) => p.id)} strategy={verticalListSortingStrategy}>
          <ul className="flex flex-col gap-1">
            {ordenados.map((paso, i) => (
              <Fila
                key={paso.id}
                paso={paso}
                numero={i + 1}
                oculto={ocultos.includes(paso.id)}
                /*
                  El ÚLTIMO paso visible no se puede ocultar.
                  Ocultándolos uno a uno se llegaba a un despliegue vivo sin
                  ninguna pregunta: el QR seguía en pie y el invitado leía «esta
                  encuesta no tiene ninguna pregunta visible ahora mismo», sin un
                  solo aviso por el camino (D12 del QA del viaje). Lo descubrió el
                  propio test ocultando de más.
                */
                // Se cuenta lo que SE VE, no «lista menos ocultos»: la lista
                // de ocultos puede traer ids que ya no están en la encuesta y
                // la resta apagaba el ojo de pasos que no eran el último.
                esElUltimoVisible={
                  !ocultos.includes(paso.id) &&
                  ordenados.filter((q) => !ocultos.includes(q.id)).length <= 1
                }
                componentesOcultos={componentesOcultos}
                onAlternarComponente={(id) =>
                  onComponentesOcultos(
                    componentesOcultos.includes(id)
                      ? componentesOcultos.filter((x) => x !== id)
                      : [...componentesOcultos, id],
                  )
                }
                activo={seleccionado === paso.id}
                abandonos={abandonos?.[paso.id] ?? 0}
                esElPeor={peor > 0 && (abandonos?.[paso.id] ?? 0) === peor}
                onSeleccionar={() => onSeleccionar(paso.id)}
                onAlternar={() =>
                  onOcultos(
                    ocultos.includes(paso.id)
                      ? ocultos.filter((x) => x !== paso.id)
                      : [...ocultos, paso.id],
                  )
                }
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>

      <p className="text-[10px] leading-snug text-[var(--color-fg-muted)]">
        {t('designJourney.stepsHint')}
      </p>
    </aside>
  );
}

function Fila({
  paso,
  numero,
  oculto,
  componentesOcultos,
  onAlternarComponente,
  activo,
  abandonos,
  esElPeor,
  onSeleccionar,
  onAlternar,
  esElUltimoVisible = false,
}: {
  paso: PasoVista;
  numero: number;
  oculto: boolean;
  /** Si es el único que queda a la vista, su ojo se apaga: ver el mapeo. */
  esElUltimoVisible?: boolean;
  componentesOcultos: number[];
  onAlternarComponente: (id: number) => void;
  activo: boolean;
  abandonos: number;
  esElPeor: boolean;
  onSeleccionar: () => void;
  onAlternar: () => void;
}) {
  const t = useT();
  const [abierto, setAbierto] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: paso.id,
  });

  const preguntas = paso.components ?? [];
  const visibles = preguntas.filter((c) => !componentesOcultos.includes(c.id));
  /*
   * Ocultas todas: el paso se va con ellas.
   *
   * No se escribe en la lista de pasos ocultos a propósito. Si se escribiera,
   * volver a enseñar una pregunta dejaría el paso oculto por una decisión que
   * nadie recuerda haber tomado; así, el paso reaparece solo. Quien manda es
   * el runtime, que lo saca del recorrido — aquí sólo se dice.
   */
  const seVaSolo = preguntas.length > 0 && visibles.length === 0;
  // Un paso legal nunca se va: el runtime lo sirve aunque figure como oculto.
  const legal = paso.legal === true;
  const noSeVe = !legal && (oculto || seVaSolo);

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex flex-col gap-1 rounded-lg border px-2 py-1.5',
        activo
          ? 'border-[var(--color-accent-ink)] bg-[var(--color-accent-10)]'
          : 'border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)]',
        isDragging && 'opacity-60',
        noSeVe && 'opacity-50',
      )}
    >
      <div className="flex items-center gap-1.5">
      <button
        type="button"
        className="cursor-grab text-[var(--color-fg-subtle)] active:cursor-grabbing"
        aria-label={t('designJourney.reorder')}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      <button
        type="button"
        onClick={onSeleccionar}
        className="min-w-0 flex-1 text-left"
        // El número es el del recorrido, no el id: quien mira la lista está
        // pensando «el tercero», no «el 587».
        title={paso.name}
      >
        <span className="mr-1.5 text-[10px] font-bold text-[var(--color-fg-subtle)]">
          {String(numero).padStart(2, '0')}
        </span>
        <span className={cn('text-sm', noSeVe && 'line-through')}>{paso.name || '—'}</span>
      </button>

      {/* Quién se fue aquí. Sin gente que se haya ido no se pinta nada: un
          «0» en cada fila es ruido que hay que leer para descartarlo. */}
      {abandonos > 0 ? (
        <span
          title={t('designJourney.abandonedHere', { n: String(abandonos) })}
          className={cn(
            'flex flex-none items-center gap-0.5 rounded px-1 text-[10px] tabular-nums',
            esElPeor
              ? 'bg-[var(--color-danger-10,rgba(239,68,68,.14))] text-[var(--color-danger-fg,#ef4444)]'
              : 'text-[var(--color-fg-subtle)]',
          )}
        >
          <UserMinus className="h-3 w-3" />
          {abandonos}
        </span>
      ) : null}

      {/* El mando avisa de dónde se elige juego: sin él hay que ir pasando
          pasos hasta dar con el que lo tiene. */}
      {paso.hasGame ? (
        <Gamepad2 className="h-3.5 w-3.5 flex-none text-[var(--color-accent-ink)]" />
      ) : null}

      {legal ? (
        /*
          Candado, no ojo. El paso lo gestiona la capa legal según lo que la
          encuesta recoge; el botón existe para que se vea POR QUÉ no hay ojo,
          y no hace nada más.
        */
        <button
          type="button"
          disabled
          className="cursor-not-allowed text-[var(--color-accent-ink)]"
          aria-label={t('designJourney.legalLocked')}
          title={t('designJourney.legalLocked')}
        >
          <Lock className="h-4 w-4" />
        </button>
      ) : (
        <button
          type="button"
          onClick={onAlternar}
          disabled={esElUltimoVisible}
          className={cn(
            'text-[var(--color-fg-subtle)] hover:text-[var(--color-fg-primary)]',
            esElUltimoVisible && 'cursor-not-allowed opacity-40 hover:text-[var(--color-fg-subtle)]',
          )}
          aria-label={
            esElUltimoVisible
              ? t('designJourney.cannotHideLast')
              : oculto
                ? t('designJourney.show')
                : t('designJourney.hide')
          }
          title={
            esElUltimoVisible
              ? t('designJourney.cannotHideLast')
              : oculto
                ? t('designJourney.show')
                : t('designJourney.hide')
          }
        >
          {oculto ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      )}
      </div>

      {/*
        Las preguntas del paso, plegadas.
        
        Plegadas por defecto porque la lista de la izquierda se lee para saber
        POR DÓNDE va la encuesta; abrir ocho pasos con seis preguntas cada uno
        convierte esa lista en un muro. Se abre el paso que se va a tocar.
      */}
      {preguntas.length > 0 ? (
        <button
          type="button"
          onClick={() => setAbierto((v) => !v)}
          className="flex items-center gap-1 self-start pl-[22px] text-[10px] text-[var(--color-fg-subtle)] hover:text-[var(--color-fg-primary)]"
        >
          {abierto ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          {t('designJourney.questionsCount', {
            n: String(visibles.length),
            total: String(preguntas.length),
          })}
        </button>
      ) : null}

      {abierto ? (
        <ul className="flex flex-col gap-0.5 pl-[22px]">
          {preguntas.map((c) => {
            const off = componentesOcultos.includes(c.id);
            // Una pregunta legal (consentimiento, puerta de edad) tampoco se
            // oculta por su cuenta: candado, como su paso.
            const preguntaLegal = c.type === 'consentForm' || c.type === 'ageGate';
            return (
              <li key={c.id} className="flex items-center gap-1.5">
                <span
                  className={cn(
                    'min-w-0 flex-1 truncate text-[11px]',
                    off
                      ? 'text-[var(--color-fg-subtle)] line-through'
                      : 'text-[var(--color-fg-muted)]',
                  )}
                  title={`${c.label} · ${c.type}`}
                >
                  {c.label}
                  {/* El asterisco importa aquí: ocultar una obligatoria es
                      justamente lo que quita el bloqueo del «continuar». */}
                  {c.required ? <span className="text-[var(--color-accent-ink)]"> *</span> : null}
                </span>
                {preguntaLegal ? (
                  <span
                    className="flex-none text-[var(--color-accent-ink)]"
                    title={t('designJourney.legalLocked')}
                    aria-label={t('designJourney.legalLocked')}
                  >
                    <Lock className="h-3.5 w-3.5" />
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => onAlternarComponente(c.id)}
                    className="flex-none text-[var(--color-fg-subtle)] hover:text-[var(--color-fg-primary)]"
                    aria-label={off ? t('designJourney.show') : t('designJourney.hide')}
                    title={off ? t('designJourney.show') : t('designJourney.hide')}
                  >
                    {off ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}

      {/* Por qué este paso no se va a ver, si nadie lo ha ocultado a mano. */}
      {seVaSolo && !oculto ? (
        <p className="pl-[22px] text-[10px] leading-snug text-[var(--color-fg-subtle)]">
          {t('designJourney.stepAutoHidden')}
        </p>
      ) : null}
    </li>
  );
}

/**
 * Memorizado a propósito. La lista no depende de los colores; sin esto se rehacía —con su contexto de arrastre— en cada tecla del panel de estilo.
 * Sus manejadores llegan estables desde `DesignJourneySection`, que es lo que
 * hace que esto sirva de algo.
 */
export const StepsPane = memo(StepsPaneBase);
